import { groupBy } from 'lodash';

import type { DeviceModel, GroupedDevices } from '../DevicesTable.types';

/**
 * Tree data row for admin devices grouped view
 */
export type AdminTreeDataRow = {
  // Group row properties
  isGroupRow?: boolean;
  deviceType?: string;

  // Device row properties (all DeviceModel fields)
  device_uid?: string;
  host_name?: string;
  display_name?: string | null;
  ip_address?: string | null;
  status?: string;
  status_label?: string;
  status_updated_at_display?: string;
  last_seen?: string | null;
  last_seen_display?: string;
  device_type_id?: number;
  device_type?: { type: string };
  organization?: { label: string };
  source_count?: number;
  source_status?: string;
  actionType?: string;
  isSyncInProgress?: boolean;

  // Tree data path
  path: string[];
};

/**
 * Converts grouped devices data into AG Grid tree data format
 *
 * Transforms devices grouped by device type into a flat array suitable
 * for AG Grid's tree data feature. Creates group rows for each device type
 * and child rows for individual devices.
 *
 * @param groupedDevices - Array of devices grouped by device type
 * @returns Flattened array suitable for AG Grid tree data
 *
 * @example
 * ```tsx
 * const treeData = convertToAdminTreeData(groupedDevices);
 * <AgGridReact rowData={treeData} treeData={true} />
 * ```
 */
export const convertToAdminTreeData = (
  groupedDevices: GroupedDevices[]
): AdminTreeDataRow[] => {
  const treeData: AdminTreeDataRow[] = [];

  groupedDevices.forEach((group) => {
    // Add group row for device type
    treeData.push({
      isGroupRow: true,
      deviceType: group.deviceType,
      host_name: group.deviceType,
      path: [group.deviceType]
    });

    // Add device rows under this group
    group.devices.forEach((device: DeviceModel) => {
      treeData.push({
        ...device,
        device_type: {
          ...device.device_type,
          type: device.device_type?.type ?? ''
        },
        isGroupRow: false,
        deviceType: group.deviceType,
        path: [group.deviceType, device.device_uid || device.host_name]
      });
    });
  });

  return treeData;
};

/**
 * Groups devices by device type
 *
 * @param devices - Array of device models
 * @returns Array of grouped devices
 *
 * @example
 * ```tsx
 * const grouped = groupDevicesByType(devices);
 * ```
 */
export const groupDevicesByType = (
  devices: DeviceModel[]
): GroupedDevices[] => {
  const groupedDevicesData = groupBy(devices, 'device_type.type');

  return Object.entries(groupedDevicesData).map(([key, deviceList]) => ({
    deviceType: key,
    devices: deviceList
  }));
};

const compareNullableNumbers = (
  valueA: number | null,
  valueB: number | null
) => {
  if (valueA == null && valueB == null) return 0;
  if (valueA == null) return -1;
  if (valueB == null) return 1;
  return valueA - valueB;
};

const parseIpv4ToNumber = (ipAddress?: string | null): number | null => {
  if (!ipAddress) return null;

  const parts = ipAddress.trim().split('.');

  if (parts.length !== 4) {
    return null;
  }

  const octets = parts.map((part) => Number(part));
  if (octets.some((octet) => Number.isNaN(octet) || octet < 0 || octet > 255)) {
    return null;
  }

  return (
    octets[0] * 256 ** 3 + octets[1] * 256 ** 2 + octets[2] * 256 + octets[3]
  );
};

/**
 * Custom comparator for IP address sorting
 *
 * Compares two IP addresses numerically by splitting them into octets.
 * Handles null/undefined values by sorting them to the beginning.
 *
 * @param valueA - First IP address
 * @param valueB - Second IP address
 * @returns Sort comparison result (-1, 0, or 1)
 *
 * @example
 * ```tsx
 * columnDef = {
 *   field: 'ip_address',
 *   comparator: ipAddressComparator
 * }
 * ```
 */
export const ipAddressComparator = (
  valueA: string | null | undefined,
  valueB: string | null | undefined
): number => {
  const parsedA = parseIpv4ToNumber(valueA);
  const parsedB = parseIpv4ToNumber(valueB);

  if (parsedA != null || parsedB != null) {
    return compareNullableNumbers(parsedA, parsedB);
  }

  return (valueA || '').localeCompare(valueB || '', undefined, {
    numeric: true,
    sensitivity: 'base'
  });
};
